/**
 * Strips Greek accent marks (tonos) and uppercases a string.
 * Source: docs/admin-redesign/phase4/04-toGreekCaps.ts
 *
 * Greek typographic convention: uppercase Greek text does NOT carry tonos.
 * `text-transform: uppercase` in CSS does not strip tonos — it just uppercases
 * the accented forms (ά → Ά), which reads as broken Greek typography.
 *
 * This utility produces the correct visual result by stripping tonos before
 * uppercasing. Dialytika (Ϊ, Ϋ) IS preserved as it represents a separate
 * phonetic mark, not an accent.
 *
 * USAGE PATTERN: store source strings as their natural mixed-case form (e.g.
 * "Συνδυασμός Επιλογών") and call `toGreekCaps()` at render time for any label
 * that should display as uppercase. Do NOT use CSS `text-transform: uppercase`
 * on Greek text.
 *
 * @example
 *   toGreekCaps('Συνδυασμός Επιλογών')   // → 'ΣΥΝΔΥΑΣΜΟΣ ΕΠΙΛΟΓΩΝ'
 *   toGreekCaps('Σήματος')               // → 'ΣΗΜΑΤΟΣ'
 *   toGreekCaps('Προϊόν')                // → 'ΠΡΟΪΟΝ'  (dialytika preserved)
 *   toGreekCaps('Ποσοστιαία Έκπτωση')    // → 'ΠΟΣΟΣΤΙΑΙΑ ΕΚΠΤΩΣΗ'
 *   toGreekCaps('Hello world')           // → 'HELLO WORLD' (Latin unaffected)
 */

const TONOS_MAP: Record<string, string> = {
	// Lowercase tonos → bare lowercase (intermediate step before uppercasing)
	ά: 'α',
	έ: 'ε',
	ή: 'η',
	ί: 'ι',
	ό: 'ο',
	ύ: 'υ',
	ώ: 'ω',
	// Uppercase tonos → bare uppercase
	Ά: 'Α',
	Έ: 'Ε',
	Ή: 'Η',
	Ί: 'Ι',
	Ό: 'Ο',
	Ύ: 'Υ',
	Ώ: 'Ω',
	// Composite forms (tonos + dialytika) → bare dialytika
	ΐ: 'ϊ',
	ΰ: 'ϋ'
};

const TONOS_REGEX = /[άέήίόύώΆΈΉΊΌΎΏΐΰ]/g;

export function toGreekCaps(str: string): string {
	if (!str) return str;
	return str.replace(TONOS_REGEX, ch => TONOS_MAP[ch] ?? ch).toUpperCase();
}

/**
 * React-friendly hook variant. Wraps `toGreekCaps` in `useMemo` for stable
 * reference across renders. Useful when a label is recomputed in a render-heavy
 * component (table cells, etc.).
 */
import { useMemo } from 'react';
export function useGreekCaps(str: string): string {
	return useMemo(() => toGreekCaps(str), [str]);
}
